import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    Tooltip,
    Progress,
    Collapse,
    IconButton,
    Grid,
    GridItem,
    Input,
    InputGroup,
    InputLeftElement,
    useBreakpointValue,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Stat,
    StatLabel,
    StatNumber,
} from '@chakra-ui/react';
import {
    FaBrain,
    FaSync,
    FaShieldAlt,
    FaRocket,
    FaUsers,
    FaAnchor,
    FaBalanceScale,
    FaMapMarkerAlt,
    FaUser,
    FaLightbulb,
    FaStar,
    FaFire,
    FaExclamationTriangle,
    FaClock,
    FaChartLine,
    FaSearch,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMsegments() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [segmentData, setSegmentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const lightBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : 'gray.100';

    // Info card backgrounds - improved transparency for dark theme
    const biasBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '10';

    // Placeholder/empty background
    const breakdownBg = isDarkTheme ? 'rgba(66, 153, 225, 0.12)' : 'gray.50';
    const nestedCardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'white';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Icon colors
    const mutedIconColor = isDarkTheme ? mutedTextColor : 'gray.400';

    // Tab colors
    const tabSelectedColor = isDarkTheme ? accentColor : primaryColor;

    const fetchSegments = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/content/rm-segments`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setSegmentData(response.data);
        } catch (err) {
            console.error('Error fetching segments:', err);
            const errorMsg = err.response?.data?.message || 'Failed to fetch customer segments';
            setError(errorMsg);
            toast({
                title: 'Error',
                description: errorMsg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchSegments();
        }
        // eslint-disable-next-line
    }, [token]);

    const getRiskColor = (riskProfile) => {
        if (riskProfile <= 3) return 'green';
        if (riskProfile <= 6) return 'yellow';
        if (riskProfile <= 8) return 'orange';
        return 'red';
    };

    const getUrgencyColor = (urgencyLevel) => {
        const level = urgencyLevel?.toLowerCase();
        if (level === 'urgent' || level === 'high') return 'red';
        if (level === 'medium' || level === 'moderate') return 'orange';
        return 'green';
    };

    const formatBiasName = (biasName) => {
        return biasName
            ?.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') || biasName;
    };

    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    const handleSearch = () => {
        // Search is handled by filtered arrays already
    };

    // Segment configurations
    const segmentConfig = [
        {
            key: 'risk_averse',
            title: 'Risk Averse',
            icon: FaShieldAlt,
            colorScheme: 'blue',
            description: 'Customers who prefer safe, conservative investments'
        },
        {
            key: 'overconfident',
            title: 'Overconfident',
            icon: FaRocket,
            colorScheme: 'red',
            description: 'Customers showing overconfidence bias in decisions'
        },
        {
            key: 'herd_followers',
            title: 'Herd Followers',
            icon: FaUsers,
            colorScheme: 'purple',
            description: 'Customers influenced by popular trends and peer behavior'
        },
        {
            key: 'anchored',
            title: 'Anchored',
            icon: FaAnchor,
            colorScheme: 'cyan',
            description: 'Customers fixated on specific reference points'
        },
        {
            key: 'balanced',
            title: 'Balanced',
            icon: FaBalanceScale,
            colorScheme: 'green',
            description: 'Customers with well-balanced decision-making patterns'
        }
    ];

    const riskAverse = segmentData?.risk_averse || [];
    const overconfident = segmentData?.overconfident || [];
    const herdFollowers = segmentData?.herd_followers || [];
    const anchored = segmentData?.anchored || [];
    const balanced = segmentData?.balanced || [];
    const totalCustomers = riskAverse.length + overconfident.length + herdFollowers.length + anchored.length + balanced.length;

    const filterCustomers = (customers) => {
        if (!searchTerm) return customers;
        return customers.filter(customer =>
            customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.country_iso?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const filteredRiskAverse = filterCustomers(riskAverse);
    const filteredOverconfident = filterCustomers(overconfident);
    const filteredHerdFollowers = filterCustomers(herdFollowers);
    const filteredAnchored = filterCustomers(anchored);
    const filteredBalanced = filterCustomers(balanced);

    const allFilteredSegments = [
        filteredRiskAverse,
        filteredOverconfident,
        filteredHerdFollowers,
        filteredAnchored,
        filteredBalanced
    ];

    const filteredTotalCustomers = filteredRiskAverse.length + filteredOverconfident.length +
        filteredHerdFollowers.length + filteredAnchored.length + filteredBalanced.length;

    // Customer Card Component
    const CustomerCard = ({ customer, colorScheme }) => {
        const isExpanded = expandedCustomer === customer.customer_id;
        
        // Generate segment-specific background color
        const segmentActionBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : `${colorScheme}.50`;

        return (
            <Card
                borderRadius="lg"
                boxShadow="sm"
                mb={3}
                borderLeft="4px solid"
                borderLeftColor={`${colorScheme}.500`}
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderColor}
            >
                <CardBody>
                    <Flex justify="space-between" align="center">
                        <VStack align="start" spacing={2} flex="1">
                            <HStack spacing={3} flexWrap="wrap">
                                <Heading size="sm" color={headingTextColor}>
                                    {customer.customer_id}
                                </Heading>
                                <Badge
                                    colorScheme={getRiskColor(customer.risk_profile)}
                                    fontSize="xs"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    fontWeight="bold"
                                >
                                    Risk: {customer.risk_profile}/10
                                </Badge>
                                <Badge
                                    colorScheme={getUrgencyColor(customer.urgency_level)}
                                    fontSize="xs"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    fontWeight="bold"
                                >
                                    {customer.urgency_level}
                                </Badge>
                            </HStack>
                            <HStack spacing={4} fontSize="sm" color={bodyTextColor} flexWrap="wrap">
                                <HStack spacing={1}>
                                    <Icon as={FaStar} boxSize={3} color={isDarkTheme ? accentColor : primaryColor} />
                                    <Text fontWeight="semibold">{customer.priority_score?.toFixed(2)}</Text>
                                </HStack>
                                <HStack spacing={1}>
                                    <Icon as={FaMapMarkerAlt} boxSize={3} />
                                    <Text>{customer.country_iso}</Text>
                                </HStack>
                                <HStack spacing={1}>
                                    <Icon as={FaUser} boxSize={3} />
                                    <Text>{customer.age} years</Text>
                                </HStack>
                            </HStack>
                        </VStack>
                        <IconButton
                            icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            onClick={() => toggleCustomer(customer.customer_id)}
                            colorScheme="blue"
                            variant="ghost"
                            aria-label="Toggle details"
                        />
                    </Flex>

                    <Collapse in={isExpanded} animateOpacity>
                        <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                            <VStack spacing={4} align="stretch">
                                {/* Recommended Action */}
                                {customer.recommended_action && (
                                    <Card 
                                        bg={segmentActionBg} 
                                        borderRadius="md" 
                                        boxShadow="sm" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack spacing={2} mb={2}>
                                                <Icon as={FaLightbulb} color={`${colorScheme}.500`} boxSize={5} />
                                                <Text fontWeight="bold" color={labelTextColor}>Recommended Action</Text>
                                            </HStack>
                                            <Text fontSize="sm" color={labelTextColor} lineHeight="1.6">
                                                {customer.recommended_action}
                                            </Text>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Top Biases */}
                                {customer.top_biases && customer.top_biases.length > 0 && (
                                    <Card 
                                        bg={biasBg} 
                                        borderRadius="md" 
                                        boxShadow="sm" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack spacing={2} mb={3}>
                                                <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                <Text fontWeight="bold" color={labelTextColor}>Top Behavioral Biases</Text>
                                            </HStack>
                                            <VStack spacing={2} align="stretch">
                                                {customer.top_biases.map((bias, index) => (
                                                    <Box 
                                                        key={index} 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <Flex justify="space-between" align="center" mb={1}>
                                                            <Text fontSize="sm" fontWeight="semibold" color={labelTextColor}>
                                                                {formatBiasName(bias.bias_name)}
                                                            </Text>
                                                            <HStack spacing={2}>
                                                                <Badge colorScheme="purple" fontSize="xs">
                                                                    Score: {bias.score}
                                                                </Badge>
                                                                <Tooltip label="Confidence Level" placement="top">
                                                                    <Badge colorScheme="green" fontSize="xs">
                                                                        {(bias.confidence * 100).toFixed(0)}%
                                                                    </Badge>
                                                                </Tooltip>
                                                            </HStack>
                                                        </Flex>
                                                        <Progress
                                                            value={bias.score}
                                                            max={100}
                                                            size="sm"
                                                            colorScheme={bias.score > 80 ? 'red' : bias.score > 60 ? 'orange' : 'yellow'}
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Breakdown Metrics */}
                                {customer.breakdown && (
                                    <Card 
                                        bg={breakdownBg} 
                                        borderRadius="md" 
                                        boxShadow="sm" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack spacing={2} mb={3}>
                                                <Icon as={FaChartLine} color="orange.500" boxSize={5} />
                                                <Text fontWeight="bold" color={labelTextColor}>Risk Breakdown</Text>
                                            </HStack>
                                            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaFire} boxSize={3} color="red.400" />
                                                                <Text fontSize="xs" color={bodyTextColor}>Bias Severity</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.bias_severity?.toFixed(1)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.bias_severity * 10}
                                                            size="xs"
                                                            colorScheme="red"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>

                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaExclamationTriangle} boxSize={3} color="orange.400" />
                                                                <Text fontSize="xs" color={bodyTextColor}>Risk Urgency</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.risk_urgency?.toFixed(2)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.risk_urgency * 10}
                                                            size="xs"
                                                            colorScheme="orange"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>

                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaClock} boxSize={3} color="blue.400" />
                                                                <Text fontSize="xs" color={bodyTextColor}>Time Sensitivity</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.time_sensitivity?.toFixed(2)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.time_sensitivity * 10}
                                                            size="xs"
                                                            colorScheme="blue"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>

                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaStar} boxSize={3} color={isDarkTheme ? accentColor : primaryColor} />
                                                                <Text fontSize="xs" color={bodyTextColor}>Customer Value</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.customer_value?.toFixed(2)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.customer_value * 10}
                                                            size="xs"
                                                            colorScheme="purple"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>
                                            </Grid>
                                        </CardBody>
                                    </Card>
                                )}
                            </VStack>
                        </Box>
                    </Collapse>
                </CardBody>
            </Card>
        );
    };

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient={isDarkTheme 
                                ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                                : `linear(to-r, ${primaryColor}, ${accentColor})`
                            }
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaBrain} boxSize={{ base: 6, md: 8 }} />
                                <Text>Behavioral Segments</Text>
                            </HStack>
                        </Heading>
                        <Text color={bodyTextColor} fontSize={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 0 }}>
                            Customers grouped by behavioral patterns and decision-making biases
                        </Text>
                    </Box>

                    {/* Statistics Cards - Clickable */}
                    {!loading && segmentData && (
                        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={{ base: 3, md: 4 }}>
                            {segmentConfig.map((config, index) => {
                                const count = segmentData[config.key]?.length || 0;
                                return (
                                    <Card
                                        key={config.key}
                                        bg={cardBg}
                                        borderRadius="xl"
                                        boxShadow="sm"
                                        border="2px"
                                        borderColor={activeTab === index ? `${config.colorScheme}.500` : lightBorderColor}
                                        cursor="pointer"
                                        onClick={() => setActiveTab(index)}
                                        _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                                        transition="all 0.2s"
                                    >
                                        <CardBody p={{ base: 3, md: 4 }}>
                                            <Stat size={statSize}>
                                                <VStack spacing={1}>
                                                    <Icon as={config.icon} color={`${config.colorScheme}.500`} boxSize={{ base: 5, md: 6 }} />
                                                    <StatLabel fontSize={{ base: '2xs', md: 'xs' }} color={bodyTextColor} textAlign="center">
                                                        {config.title}
                                                    </StatLabel>
                                                    <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color={`${config.colorScheme}.600`} fontWeight="bold">
                                                        {count}
                                                    </StatNumber>
                                                </VStack>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                );
                            })}
                        </SimpleGrid>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert 
                            status="error" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            boxShadow="sm" 
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontSize={{ base: 'sm', md: 'md' }} color={headingTextColor}>Error!</AlertTitle>
                                <AlertDescription fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color={mutedIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID or Country..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor={borderColor}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    bg={primaryColor}
                                    color="white"
                                    onClick={fetchSegments}
                                    isLoading={loading}
                                    _hover={{ bg: buttonHoverBg }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Tabbed Segment Lists */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor} fontSize="sm">Loading behavioral segments...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredTotalCustomers > 0 || totalCustomers > 0 ? (
                        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
                            <TabList flexWrap="wrap">
                                {segmentConfig.map((config, index) => (
                                    <Tab 
                                        key={config.key} 
                                        _selected={{ 
                                            color: tabSelectedColor, 
                                            borderColor: tabSelectedColor 
                                        }}
                                        color={bodyTextColor}
                                    >
                                        <HStack spacing={2}>
                                            <Icon as={config.icon} color={`${config.colorScheme}.500`} />
                                            <Text>{config.title}</Text>
                                            <Badge colorScheme={config.colorScheme} borderRadius="full">
                                                {allFilteredSegments[index].length}
                                            </Badge>
                                        </HStack>
                                    </Tab>
                                ))}
                            </TabList>

                            <TabPanels>
                                {segmentConfig.map((config, index) => (
                                    <TabPanel key={config.key} px={0} pt={6}>
                                        {/* Segment Description */}
                                        <Card 
                                            bg={isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : `${config.colorScheme}.50`} 
                                            borderRadius="md" 
                                            boxShadow="sm" 
                                            mb={4} 
                                            borderWidth="1px" 
                                            borderColor={borderColor}
                                        >
                                            <CardBody>
                                                <HStack spacing={2}>
                                                    <Icon as={config.icon} color={`${config.colorScheme}.500`} boxSize={5} />
                                                    <Text fontSize="sm" color={labelTextColor} fontStyle="italic">
                                                        {config.description}
                                                    </Text>
                                                </HStack>
                                            </CardBody>
                                        </Card>

                                        {/* Customer List */}
                                        {allFilteredSegments[index].length > 0 ? (
                                            <VStack spacing={0} align="stretch">
                                                {allFilteredSegments[index].map((customer, idx) => (
                                                    <CustomerCard
                                                        key={customer.customer_id || idx}
                                                        customer={customer}
                                                        colorScheme={config.colorScheme}
                                                    />
                                                ))}
                                            </VStack>
                                        ) : (
                                            <Box textAlign="center" py={10}>
                                                <Icon as={searchTerm ? FaSearch : config.icon} boxSize={12} color={mutedIconColor} mb={4} />
                                                <Text color={mutedTextColor} fontSize="lg" fontWeight="medium">
                                                    {searchTerm
                                                        ? `No ${config.title.toLowerCase()} customers found matching "${searchTerm}"`
                                                        : `No ${config.title.toLowerCase()} customers`
                                                    }
                                                </Text>
                                                {searchTerm && (
                                                    <Button
                                                        mt={4}
                                                        variant="link"
                                                        color={isDarkTheme ? accentColor : primaryColor}
                                                        onClick={() => setSearchTerm('')}
                                                    >
                                                        Clear search
                                                    </Button>
                                                )}
                                            </Box>
                                        )}
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </Tabs>
                    ) : (
                        <Card 
                            borderRadius="lg" 
                            boxShadow="md" 
                            bg={cardBg} 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Box textAlign="center" py={10}>
                                    <Icon as={FaBrain} boxSize={{ base: 10, md: 12 }} color={mutedIconColor} mb={4} />
                                    <Text color={mutedTextColor} fontSize={{ base: 'md', md: 'lg' }} fontWeight="medium">
                                        No behavioral segments available
                                    </Text>
                                    <Text color={mutedTextColor} fontSize="sm" mt={2}>
                                        Customer behavioral patterns will appear here once analyzed
                                    </Text>
                                </Box>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMsegments;